import React, {useCallback, useEffect, useMemo, useState} from "react"
import {api} from "@indebted/api"
import {useDebounce} from "@indebted/hooks"
import {ErrorPage} from "@indebted/components/Error"
import {useNotification} from "@indebted/components/Notification"
import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	CircularProgress,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Typography,
} from "@mui/material"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ErrorIcon from "@mui/icons-material/Error"

function buildTemplateMap(templates) {
	const map = new Map()
	templates.forEach((t) => map.set(t.ID, t))
	return map
}

function buildTextareaContent(templates) {
	return templates
		.filter((t) => t.SequenceNumber > 0)
		.sort((a, b) => a.SequenceNumber - b.SequenceNumber)
		.map((t) => t.ID)
		.join("\n")
}

// eslint-disable-next-line complexity
function validateLines(text, templateMap) {
	const lines = text.split("\n")
	const result = []
	const seen = new Set()
	let hasErrors = false

	let lineNumber = 0
	for (const raw of lines) {
		const uuid = raw.trim()
		if (!uuid) continue

		lineNumber++
		const template = templateMap.get(uuid) || null
		let error = null

		if (!template) {
			error = "Unknown template"
		} else if (seen.has(uuid)) {
			error = "Duplicate template"
		}

		if (error) hasErrors = true
		seen.add(uuid)
		result.push({lineNumber, uuid, template, error})
	}

	return {lines: result, hasErrors}
}

function buildSequencesFromLines(lines) {
	return lines.map((line) => ({
		TemplateID: line.uuid,
		SequenceNumber: line.lineNumber,
	}))
}

function LocaleSelector({locale, locales, onChange}) {
	return (
		<Box display="flex" gap={2} mb={3}>
			<FormControl sx={{minWidth: 150}}>
				<InputLabel>Locale</InputLabel>
				<Select value={locale} label="Locale" onChange={(e) => onChange(e.target.value)}>
					{locales.map((l) => (
						<MenuItem key={l} value={l}>
							{l}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</Box>
	)
}

function previewLineProps(line) {
	if (line.error) {
		return {
			Icon: ErrorIcon,
			iconColor: "error",
			uuidColor: "error.main",
			detail: `— ${line.error}`,
			detailColor: "error",
		}
	}
	return {
		Icon: CheckCircleIcon,
		iconColor: "success",
		uuidColor: "text.primary",
		detail: `— ${line.template.Subject} (${line.template.ForValidationPeriod})`,
		detailColor: "text.secondary",
	}
}

function PreviewLine({line}) {
	const {Icon, iconColor, uuidColor, detail, detailColor} = previewLineProps(line)

	return (
		<Box display="flex" alignItems="center" gap={1} py={0.5} px={1}>
			<Icon color={iconColor} fontSize="small" />
			<Typography variant="body2" sx={{fontFamily: "monospace", fontSize: "0.8rem", minWidth: 30}}>
				#{line.lineNumber}
			</Typography>
			<Typography variant="body2" sx={{fontFamily: "monospace", fontSize: "0.8rem", color: uuidColor}}>
				{line.uuid}
			</Typography>
			<Typography variant="body2" color={detailColor} noWrap>
				{detail}
			</Typography>
		</Box>
	)
}

function PreviewPanel({validation}) {
	if (!validation) {
		return (
			<Typography color="text.secondary" p={2}>
				Paste template UUIDs to see preview...
			</Typography>
		)
	}

	if (validation.lines.length === 0) {
		return (
			<Typography color="text.secondary" p={2}>
				No template IDs entered
			</Typography>
		)
	}

	return (
		<Box>
			{validation.lines.map((line, index) => (
				<PreviewLine key={index} line={line} />
			))}
		</Box>
	)
}

// eslint-disable-next-line complexity
function SequencingPage() {
	const [locale, setLocale] = useState("en-AU")
	const [locales, setLocales] = useState([])
	const [templates, setTemplates] = useState(null)
	const [text, setText] = useState("")
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState(null)
	const {notification} = useNotification()

	const templateMap = useMemo(() => {
		return templates ? buildTemplateMap(templates) : new Map()
	}, [templates])

	const debouncedText = useDebounce(text, 300)

	const validation = useMemo(() => {
		if (!templates) return null
		return validateLines(debouncedText, templateMap)
	}, [debouncedText, templateMap, templates])

	const load = useCallback(() => {
		if (!locale) return
		setLoading(true)
		setError(null)
		api.templateSequences
			.get("Email", locale)
			.then((data) => {
				setLocales(data.Locales)
				setTemplates(data.Templates)
				setText(buildTextareaContent(data.Templates))
			})
			.catch((err) => setError(err.Message))
			.finally(() => setLoading(false))
	}, [locale])

	useEffect(load, [load])

	const handleSave = () => {
		if (!validation || validation.hasErrors) return

		const seqList = buildSequencesFromLines(validation.lines)

		setSaving(true)
		api.templateSequences
			.save("Email", locale, seqList)
			.then(() => {
				notification.success("Template ordering saved")
				load()
			})
			.catch((err) => notification.error(err.Message))
			.finally(() => setSaving(false))
	}

	if (error) return <ErrorPage message={error} />

	return (
		<Grid container spacing={3}>
			<Grid item xs={6}>
				<Card sx={{height: "100%", display: "flex", flexDirection: "column"}}>
					<CardHeader title="Email Template Ordering" />
					<CardContent sx={{flex: 1, display: "flex", flexDirection: "column"}}>
						{locales.length > 0 && (
							<LocaleSelector locale={locale} locales={locales} onChange={setLocale} />
						)}
						{loading && (
							<Box display="flex" justifyContent="center" p={4}>
								<CircularProgress />
							</Box>
						)}
						{templates && !loading && (
							<Box
								component="textarea"
								value={text}
								onChange={(e) => setText(e.target.value)}
								placeholder="Paste template UUIDs here, one per line..."
								sx={{
									flex: 1,
									minHeight: 500,
									fontFamily: "monospace",
									fontSize: "0.85rem",
									lineHeight: 1.6,
									p: "12px",
									border: "1px solid #ccc",
									borderRadius: 1,
									resize: "vertical",
									width: "100%",
									boxSizing: "border-box",
								}}
							/>
						)}
					</CardContent>
					{templates && !loading && (
						<CardActions>
							<Box display="flex" justifyContent="flex-end" alignItems="center" width="100%">
								<Typography variant="body2" color="text.secondary" sx={{mr: "auto"}}>
									{validation ? `${validation.lines.length} template(s)` : ""}
								</Typography>
								<Button
									variant="contained"
									onClick={handleSave}
									disabled={
										saving || !validation || validation.hasErrors || validation.lines.length === 0
									}
								>
									{saving ? "Saving..." : "Save Ordering"}
								</Button>
							</Box>
						</CardActions>
					)}
				</Card>
			</Grid>
			<Grid item xs={6}>
				<Card sx={{height: "100%", display: "flex", flexDirection: "column"}}>
					<CardHeader title="Preview" />
					<CardContent sx={{flex: 1, overflowY: "auto", maxHeight: 650}}>
						<PreviewPanel validation={validation} />
					</CardContent>
				</Card>
			</Grid>
		</Grid>
	)
}

export {SequencingPage}
